/**
 * Basecamp Single Todo Viewer (Refactored)
 * Main orchestrator component - delegates to sub-components and hooks
 *
 * Phase 4 of Basecamp Integration
 * Related: docs/implementations/2025-10-22-basecamp-my-stuff-integration.md
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';
import { SecondaryPreviewResizeHandle } from '@/components/integrations/_shared';
import { Button } from '@/components/ui/button';
import { AlertCircle, Loader2 } from 'lucide-react';
import type { BasecampTodo } from '@/types/basecamp';
import { ABTestViewer } from '@/components/features/ab-test-viewer';
import { DiffPreviewDialog } from '@/components/layout/diff-preview-dialog';
import { useIngestionABTestSession } from '@/hooks/use-ingestion-ab-test-session';
import { useBasecampTodoLists } from '@/hooks/use-basecamp';
import { useContainerWidth } from '@/hooks/use-container-width';
import {
  buildBasecampBreadcrumbTrail,
  dispatchIntegrationTabEnrich,
} from '@/lib/basecamp-breadcrumbs';
import { useBasecampTodoData } from './hooks/use-basecamp-todo-data';
import { useBasecampTodoRefreshEvents } from './hooks/use-basecamp-todo-refresh-events';
import { BasecampTodoListViewer } from './basecamp-todo-list-viewer';
import { useTodoHandlers } from './handlers/todo-handlers';
import { BasecampCommentStyles } from './styles/basecamp-comment-styles';
import { TodoMetadataGrid } from './components/todo-metadata-grid';
import { TodoDescription } from './components/todo-description';
import { TodoSubtasksSection } from './components/todo-subtasks-section';
import { TodoWorkSpecsSection } from './components/todo-work-specs-section';
import { TodoCommentsSection } from './components/todo-comments-section';
import { TodoActionsSection } from './components/todo-actions-section';
import { TodoSystemConnections } from './components/todo-system-connections';
import { DocumentPreviewPanel } from './components/document-preview-panel';
import { GitPathConfig } from './components/git-path-config';
import { SecondaryPanel } from '@/components/layout/secondary-panel';
import { MinimizedSection } from '@/components/layout/minimized-section';
import { PANEL_SIZES } from '@/lib/panel-sizes';
import { testIds } from '@/lib/test-ids';

interface BasecampTodoViewerProps {
  todoId: string;
  projectId?: string;
  projectName?: string;
  organizationId?: string;
  onDocumentClick?: (documentId: string, openInNewTab?: boolean | React.MouseEvent) => Promise<void>;
  onGoBack?: () => void;
  onGoForward?: () => void;
  canGoBack?: boolean;
  canGoForward?: boolean;
  onTodoLoad?: (todo: BasecampTodo) => void;
  onPreviewDocument?: (filePath: string) => void;
  onTitleVisibilityChange?: (isVisible: boolean) => void;
  openTabs?: Array<{ id: string; isPinned?: boolean }>;
  activeTabId?: string | null;
  /** When true, document-viewer's right panel (info/edit) is expanded; use stacked layout. When false + xl viewport, use 2-column layout. */
  rightPanelExpanded?: boolean;
  /** Scroll/highlight a BC5 subtask row when opening parent from step assignment or deep link */
  highlightStepId?: string;
}

// Export RelatedDocument type for use in components
export interface RelatedDocument {
  id: string;
  title: string;
  type: string;
  status: string;
  excerpt: string;
  tags: string[];
  projectName: string;
  updatedBy: string;
  updatedAt: string;
  filePath: string;
  matchScore: number;
}

export function BasecampTodoViewer({
  todoId,
  projectId,
  projectName: propProjectName,
  organizationId,
  onDocumentClick,
  onTodoLoad,
  openTabs = [],
  activeTabId = null,
  rightPanelExpanded = false,
  highlightStepId,
}: BasecampTodoViewerProps) {
  // A/B test state
  const [showABTest, setShowABTest] = useState(false);
  const [abTestData, setAbTestData] = useState<{ optionA: any; optionB: any } | null>(null);
  const [isLoadingABTest, setIsLoadingABTest] = useState(false);
  const [abTestSessionId, setAbTestSessionId] = useState<string | null>(null);

  // Diff preview state
  const [showDiff, setShowDiff] = useState(false);
  const [diffData, setDiffData] = useState<{ original: string; proposed: string; filename: string } | null>(null);
  const [isApplyingDiff, setIsApplyingDiff] = useState(false);

  // Document preview state
  const [previewDocument, setPreviewDocument] = useState<{ filePath: string; title?: string } | null>(null);

  // Promotion state for sections (comments, related, work-specs, actions)
  const [promotedSection, setPromotedSection] = useState<'comments' | 'related' | 'work-specs' | 'actions' | null>(null);

  // Check for pending A/B test session (for future use)
  useIngestionABTestSession(todoId);

  // Data fetching hook
  const todoData = useBasecampTodoData({
    todoId,
    projectId,
    projectName: propProjectName,
    onTodoLoad
  });

  // Todo lists for this project - reused (with its own localStorage cache) to build the
  // full ancestor breadcrumb trail (project > todos > [nested todolists] > parent todolist)
  // via the same buildBasecampBreadcrumbTrail() helper the todolist viewer uses.
  const { todoLists } = useBasecampTodoLists(projectId || null);

  const projectName = todoData.projectName || propProjectName;

  // Build breadcrumb trail ending at the todo's immediate parent todolist, then append the
  // todo itself (handled by BasecampIntegrationBreadcrumbs for 'integration-basecamp-todo' tabs).
  const breadcrumbTrail = useMemo(() => {
    const parentListId = todoData.todo?.parent?.id?.toString();
    if (!projectId || !projectName || !parentListId || todoLists.length === 0) return [];
    return buildBasecampBreadcrumbTrail({
      projectId,
      projectName,
      listId: parentListId,
      todoLists,
    });
  }, [projectId, projectName, todoData.todo?.parent?.id, todoLists]);

  // Enrich tab with the full ancestor trail + resolved title for the chrome-level breadcrumb.
  useEffect(() => {
    if (!todoData.todo || breadcrumbTrail.length === 0) return;
    dispatchIntegrationTabEnrich({
      tabId: `basecamp-todo-${todoId}`,
      breadcrumbTrail,
      title: todoData.todo.title,
      projectName: projectName || undefined,
    });
  }, [todoId, todoData.todo, breadcrumbTrail, projectName]);

  // Listen for refresh / ingestion / document link events
  useBasecampTodoRefreshEvents({
    todoId,
    onRefreshRelatedDocs: () => {
      void todoData.loadRelatedDocuments();
    },
    onFullRefresh: () => {
      console.log('[BasecampTodoViewer] Refresh triggered from menu');
      void todoData.loadTodoDetails(true).then(() => todoData.loadComments());
      void todoData.loadRelatedDocuments();
    },
    onIngestionComplete: () => {
      console.log('[BasecampTodoViewer] Ingestion complete, refreshing work specs');
      void todoData.loadRelatedDocuments();
      void todoData.loadConnections();
    },
  });

  // Lazy-load comments when COMMENTS section is promoted to secondary panel.
  // Use commentsLoaded (not comments.length) so an empty thread does not re-fetch forever.
  useEffect(() => {
    if (promotedSection !== 'comments' || !todoData.todo || !projectId) return;
    if (todoData.commentsLoaded || todoData.commentsLoadFailed || todoData.isLoadingComments) {
      return;
    }
    void todoData.loadComments();
  }, [
    promotedSection,
    projectId,
    todoData.todo?.id,
    todoData.commentsLoaded,
    todoData.isLoadingComments,
    todoData.commentsLoadFailed,
    todoData.loadComments,
  ]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ todoId?: string }>).detail
      if (detail?.todoId && String(detail.todoId) !== String(todoId)) return
      setPromotedSection('comments')
    }
    window.addEventListener('basecamp-todo-promote-comments', handler)
    return () => window.removeEventListener('basecamp-todo-promote-comments', handler)
  }, [todoId]);

  // Event handlers hook
  const handlers = useTodoHandlers({
    todo: todoData.todo,
    projectId,
    todoId,
    organizationId,
    comments: todoData.comments,
    relatedDocs: todoData.relatedDocs,
    abTestData,
    abTestSessionId,
    diffData,
    setAbTestData,
    setShowABTest,
    setIsLoadingABTest,
    setDiffData,
    setShowDiff,
    setIsApplyingDiff,
    loadRelatedDocuments: todoData.loadRelatedDocuments,
    onDocumentClick
  });

  // Actions widget can still dispatch this event (overflow no longer owns Update Work Spec).
  // Same event-bridge pattern as 'basecamp-todo-refresh' above.
  useEffect(() => {
    const handleUpdateWorkSpec = (event: CustomEvent) => {
      const eventTodoId = event.detail?.todoId;
      if (eventTodoId === todoId || eventTodoId === undefined) {
        handlers.handleIngestOrUpdate();
      }
    };

    window.addEventListener('basecamp-todo-update-work-spec', handleUpdateWorkSpec as EventListener);
    return () => {
      window.removeEventListener('basecamp-todo-update-work-spec', handleUpdateWorkSpec as EventListener);
    };
  }, [todoId, handlers]);

  // Update session ID when A/B test data is set
  useEffect(() => {
    const sessionId = abTestData && ((abTestData as any).sessionId || (abTestData as any).session_id);
    if (sessionId && !abTestSessionId) {
      setAbTestSessionId(sessionId);
    }
  }, [abTestData, abTestSessionId]);

  // Wide vs narrow layout for the description's collapse behavior (see TodoDescription):
  // same @[860px] container-width threshold the grid below already uses to switch between
  // its 1-col "stacked" and 2-col "responsive" layouts. Treat "not yet measured" as wide so
  // we do not flash a clamped/faded description on desktop before ResizeObserver attaches.
  const scrollRegionRef = useRef<HTMLDivElement>(null);
  const scrollRegionWidth = useContainerWidth(scrollRegionRef);
  const isWideDescriptionLayout =
    !rightPanelExpanded &&
    (scrollRegionWidth == null || scrollRegionWidth >= 860);

  // Loading state
  if (todoData.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading todo...</p>
      </div>
    );
  }

  // If the ID belongs to a todolist rather than a single todo, render the list viewer
  if (todoData.isNotFound) {
    return (
      <BasecampTodoListViewer
        listId={todoId}
        projectId={projectId || ''}
        projectName={propProjectName}
        organizationId={organizationId}
        onDocumentClick={onDocumentClick as any}
      />
    );
  }

  // Error state
  if (todoData.error || !todoData.todo) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 p-6">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold">Todo Not Available</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            {todoData.error ||
              'Unable to load this todo. It may have been deleted or you may not have access.'}
          </p>
          <div className="pt-4 space-y-2">
            <p className="text-xs text-muted-foreground">
              <strong>Todo ID:</strong> {todoId}
              {projectId ? (
                <>
                  {' '}
                  &middot; <strong>Project:</strong> {projectId}
                </>
              ) : null}
            </p>
            <p className="text-xs text-muted-foreground">
              Check Basecamp connection in Settings, or open the work spec from search if this todo
              was already ingested.
            </p>
          </div>
        </div>
        <Button
          onClick={(e) => {
            e.preventDefault();
            todoData.loadTodoDetails(true);
          }}
          variant="outline"
          className="mt-4"
        >
          Try Again
        </Button>
      </div>
    );
  }

  const todoStaleFromCache = Boolean(
    (todoData.todo as { _flownotes_stale?: boolean } | null)?._flownotes_stale
  );

  return (
    <div className="flex flex-col h-full w-full bg-background" data-testid={testIds.basecamp.todoViewer} data-todo-id={todoId}>
      <BasecampCommentStyles />

      {todoStaleFromCache ? (
        <div className="mx-6 mt-4 rounded-md border border-yellow-500/30 bg-yellow-500/10 px-4 py-2 text-sm text-yellow-800 dark:text-yellow-200">
          Showing cached Basecamp data. Live API returned{' '}
          {(todoData.todo as { _flownotes_basecamp_status?: number })?._flownotes_basecamp_status ??
            'an error'}
          ; use Try Again after checking access in Basecamp.
        </div>
      ) : null}

      {/* Split Panel Layout */}
      <PanelGroup direction="horizontal" className="flex-1 overflow-hidden">
        {/* LEFT PANEL - Main Content */}
        <Panel
          defaultSize={promotedSection ? 60 : 100}
          minSize={promotedSection ? 40 : 100}
          maxSize={promotedSection ? undefined : 100}
          className="overflow-hidden flex flex-col"
        >
          {/* @container — grid switches to 2-col at 860px of available panel width */}
          <div ref={scrollRegionRef} className="flex-1 overflow-y-auto min-h-0 @container" data-testid={testIds.basecamp.todoViewerScrollRegion}>
            {/* Hidden GitPathConfig - listens for 'basecamp-project-configure-git' from Cursor dropdown */}
            {projectId && (
              <div className="hidden">
                <GitPathConfig
                  projectId={projectId}
                  projectName={todoData.projectName || propProjectName || 'Project'}
                  variant="badge"
                />
              </div>
            )}
            <div
              className={`px-6 py-8 grid gap-8 grid-cols-1 ${!rightPanelExpanded ? '@[860px]:grid-cols-[minmax(480px,1fr)_320px] @[860px]:gap-8' : ''}`}
              data-testid={testIds.basecamp.todoViewerGrid}
              data-layout={!rightPanelExpanded ? 'responsive' : 'stacked'}
            >
              {/* Details column - max-w-3xl caps line length on very wide screens */}
              <div className="min-w-0 space-y-6 max-w-3xl" data-testid={testIds.basecamp.todoViewerDetails}>
                <TodoMetadataGrid
                  todo={todoData.todo!}
                  onOpenComments={() => setPromotedSection('comments')}
                />

                <TodoDescription
                  todo={todoData.todo!}
                  isWideLayout={isWideDescriptionLayout}
                  projectId={projectId}
                  onPull={() => todoData.loadTodoDetails(true)}
                />

                <TodoSubtasksSection
                  todo={todoData.todo!}
                  projectId={projectId}
                  highlightStepId={highlightStepId}
                  onStepsUpdated={() => todoData.loadTodoDetails(true)}
                />
              </div>

              {/* Widgets column - Comments, Work Specs, Related, Actions */}
              <div className="space-y-4" data-testid={testIds.basecamp.todoViewerWidgets}>
              {/* Comments Section */}
              <MinimizedSection
                type="comments"
                title="Comments"
                count={todoData.comments.length || todoData.todo?.comments_count || 0}
                preview={
                  todoData.comments.length > 0 ? (
                    <div className="flex items-start gap-2 text-xs">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground flex-shrink-0">
                        {(todoData.comments[0]?.creator?.name || 'U')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">
                          {todoData.comments[0]?.creator?.name || 'Unknown'}
                        </p>
                        <p className="text-xs text-muted-foreground line-clamp-1">
                          {todoData.comments[0]?.content?.replace(/<[^>]*>/g, '').substring(0, 60)}...
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">No comments yet</p>
                  )
                }
                onPromote={() => setPromotedSection('comments')}
                onDemote={() => setPromotedSection(null)}
                isPromoted={promotedSection === 'comments'}
              />

              {/* Work Specs Section */}
              <MinimizedSection
                type="work-specs"
                title="Work Specs"
                count={todoData.relatedDocs.length}
                preview={
                  todoData.relatedDocs.length > 0 ? (
                    (() => {
                      const previewDoc = [...todoData.relatedDocs].sort((a, b) => {
                        const aIsReadme = a.filePath?.split('/').pop()?.toLowerCase() === 'readme.md';
                        const bIsReadme = b.filePath?.split('/').pop()?.toLowerCase() === 'readme.md';
                        if (aIsReadme && !bIsReadme) return -1;
                        if (!aIsReadme && bIsReadme) return 1;
                        return (b.matchScore || 0) - (a.matchScore || 0);
                      })[0];
                      if (!previewDoc) return <p className="text-xs text-muted-foreground">No work specs found</p>;
                      return (
                        <div className="space-y-1">
                          <p className="text-muted-foreground truncate text-xs">{previewDoc.title}</p>
                          <div className="flex items-center gap-1 mt-1">
                            {previewDoc.status && (
                              <span className="text-[10px] px-1 py-0 bg-muted rounded">{previewDoc.status}</span>
                            )}
                            <span className="text-muted-foreground text-xs">{previewDoc.projectName || 'Unknown'}</span>
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <p className="text-xs text-muted-foreground">No work specs found</p>
                  )
                }
                onPromote={() => setPromotedSection('work-specs')}
                onDemote={() => setPromotedSection(null)}
                isPromoted={promotedSection === 'work-specs'}
              />

              {/* Related Section */}
              <MinimizedSection
                type="related"
                title="Related"
                count={
                  todoData.relatedDocs.length +
                  todoData.connections.semantic.length +
                  todoData.connections.casual.length +
                  (todoData.connections.todos?.length ?? 0)
                }
                preview={
                  (todoData.relatedDocs.length > 0 ||
                    todoData.connections.semantic.length > 0 ||
                    todoData.connections.casual.length > 0 ||
                    (todoData.connections.todos?.length ?? 0) > 0) ? (
                    <div className="space-y-1">
                      {[
                        ...todoData.relatedDocs.slice(0, 1).map((d) => ({ id: d.id ?? d.filePath, title: d.title })),
                        ...(todoData.connections.todos ?? []).slice(0, 1).map((t) => ({ id: t.id, title: t.title })),
                        ...todoData.connections.semantic.slice(0, 1).map((d) => ({ id: d.id, title: d.title })),
                        ...todoData.connections.casual.slice(0, 1).map((d) => ({ id: d.id, title: d.title }))
                      ]
                        .slice(0, 2)
                        .map((item) => (
                          <div key={item.id} className="flex items-center gap-2 text-xs">
                            <span className="text-muted-foreground truncate">{item.title}</span>
                          </div>
                        ))}
                      {(todoData.relatedDocs.length +
                        todoData.connections.semantic.length +
                        todoData.connections.casual.length +
                        (todoData.connections.todos?.length ?? 0)) > 2 && (
                        <p className="text-xs text-muted-foreground">
                          +
                          {(todoData.relatedDocs.length +
                            todoData.connections.semantic.length +
                            todoData.connections.casual.length +
                            (todoData.connections.todos?.length ?? 0)) -
                            2}{' '}
                          more
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">No related documents or todos</p>
                  )
                }
                onPromote={() => setPromotedSection('related')}
                onDemote={() => setPromotedSection(null)}
                isPromoted={promotedSection === 'related'}
              />

              {/* Actions Section */}
              <MinimizedSection
                type="actions"
                title="Actions"
                count={todoData.relatedDocs.length > 0 ? 7 : 2}
                preview={
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      {todoData.relatedDocs.length > 0 ? 'Update Work Spec' : 'Create work spec'}
                    </p>
                    <p className="text-xs text-muted-foreground opacity-60">
                      {todoData.relatedDocs.length > 0 ? '+ more actions' : 'Add to current chat'}
                    </p>
                  </div>
                }
                onPromote={() => setPromotedSection('actions')}
                onDemote={() => setPromotedSection(null)}
                isPromoted={promotedSection === 'actions'}
              />
              </div>
            </div>
          </div>
        </Panel>

        {/* Resizable Handle - Only show when section is promoted */}
        {promotedSection && (
          <>
            <SecondaryPreviewResizeHandle />

            {/* RIGHT PANEL - Promoted Section OR Document Preview */}
            <SecondaryPanel
              panelType={previewDocument ? 'basecamp-document-preview' : promotedSection === 'comments' ? 'basecamp-comments' : promotedSection === 'related' ? 'basecamp-related' : promotedSection === 'actions' ? 'basecamp-actions' : 'basecamp-work-specs'}
              panelId="basecamp-todo-secondary"
              defaultSize={PANEL_SIZES.SECONDARY_DEFAULT}
              minSize={PANEL_SIZES.SECONDARY_MIN}
            >
              {previewDocument ? (
                <DocumentPreviewPanel
                  filePath={previewDocument.filePath}
                  title={previewDocument.title}
                  onClose={() => setPreviewDocument(null)}
                  onEdit={async (filePath) => {
                    // Edit: Open document in new tab for editing
                    if (onDocumentClick) {
                      const syntheticEvent = {
                        metaKey: true,
                        ctrlKey: false,
                        preventDefault: () => {},
                        stopPropagation: () => {}
                      } as any;
                      await onDocumentClick(filePath, syntheticEvent);
                    }
                  }}
                />
              ) : promotedSection === 'comments' ? (
                <TodoCommentsSection
                  todo={todoData.todo!}
                  comments={todoData.comments}
                  isLoadingComments={todoData.isLoadingComments}
                  projectId={projectId}
                  onDemote={() => setPromotedSection(null)}
                  onPostComment={todoData.postComment}
                  isPostingComment={todoData.isPostingComment}
                />
              ) : promotedSection === 'related' ? (
                <TodoSystemConnections
                  connections={todoData.connections}
                  onDocumentClick={onDocumentClick}
                  projectId={projectId}
                  onDemote={() => setPromotedSection(null)}
                />
              ) : promotedSection === 'actions' ? (
                <TodoActionsSection
                  todo={todoData.todo!}
                  projectId={projectId}
                  projectName={todoData.projectName || propProjectName}
                  todoId={todoId}
                  onIngestOrUpdate={handlers.handleIngestOrUpdate}
                  onDemote={() => setPromotedSection(null)}
                  onDocumentClick={onDocumentClick}
                  onOpenComments={() => setPromotedSection('comments')}
                  relatedDocs={todoData.relatedDocs}
                />
              ) : promotedSection === 'work-specs' ? (
                <TodoWorkSpecsSection
                  relatedDocs={todoData.relatedDocs}
                  isLoadingDocs={todoData.isLoadingDocs}
                  onDocumentClick={onDocumentClick}
                  onPreviewDocument={(filePath: string, title?: string) => {
                    setPreviewDocument({ filePath, title });
                  }}
                  onDemote={() => setPromotedSection(null)}
                  openTabs={openTabs}
                  activeTabId={activeTabId}
                />
              ) : null}
            </SecondaryPanel>
          </>
        )}
      </PanelGroup>

      {/* A/B Test Modal */}
      <ABTestViewer
        open={showABTest}
        onOpenChange={setShowABTest}
        optionA={abTestData?.optionA || null}
        optionB={abTestData?.optionB || null}
        isLoading={isLoadingABTest}
        onSelect={handlers.handleABTestSelect}
      />

      {/* Diff Preview Dialog */}
      {diffData && (
        <DiffPreviewDialog
          open={showDiff}
          onOpenChange={setShowDiff}
          original={diffData.original}
          proposed={diffData.proposed}
          filename={diffData.filename}
          onAccept={handlers.handleDiffAccept}
          onReject={handlers.handleDiffReject}
          isApplying={isApplyingDiff}
        />
      )}
    </div>
  );
}

